import { useEffect, useLayoutEffect, useState } from "react";
import { useAppContext } from "../../Context"
import { Clock4, ChartNoAxesColumnIncreasing, Shapes, Building } from "lucide-react"
import FilterSidebar from "../../components/training/FilterSidebar";
import EmployeeTrainingTable from "../../components/training/EmployeeTrainingTable";
import { ProviderTrainingCompliance, ProviderTrainingComplianceEntry, TrainingCompliance, TrainingComplianceEntry } from "../../types/training";
import { getTrainingCompliance, getProviderTrainingCompliance } from "../../apicalls/trainingService";
import TrainingComplianceDashboard from "../../components/training/TrainingComplianceTable";
import TrainingComplianceComparison from "../../components/training/TrainingComplianceComparison";
import ProviderTrainingComplianceDashboard from "../../components/training/ProviderTrainingComplianceTable";

const TrainingDashboardPage = props => {
    const navContext = useAppContext();
    const [complianceType, setComplianceType] = useState<'client' | 'provider'>('client');

    useLayoutEffect(() => {
        navContext.setFullScreen(true);
    }, []);

    const [dashboardType, setDashboardType] = useState<string>("client"); // client or provider
    
    const [complianceData, setComplianceData] = useState<TrainingComplianceEntry[]>([]);
    const [providerComplianceData, setProviderComplianceData] = useState<ProviderTrainingComplianceEntry[]>([]);
    const [complianceDataLoading, setComplianceDataLoading] = useState(true);
    const [providerComplianceDataLoading, setProviderComplianceDataLoading] = useState(true);
    const [trainingCompliance, setTrainingCompliance] = useState<TrainingCompliance[]>([]);
    const [providerTrainingCompliance, setProviderTrainingCompliance] = useState<ProviderTrainingCompliance[]>([]);

    const [overallCompliance, setOverallCompliance] = useState(0);
    const [expiringCompliance, setExpiringCompliance] = useState(0);
    const [expiredCompliance, setExpiredCompliance] = useState(0);

    // Provider dashboard metrics
    const [averageProviderCompliance, setAverageProviderCompliance] = useState(0);
    const [providersNeedingAction, setProvidersNeedingAction] = useState(0);
    const [totalProviders, setTotalProviders] = useState(0);

    useEffect(() => {
        const fetchComplianceData = async () => {
            setComplianceDataLoading(true);
            const data = await getTrainingCompliance(navContext.handleAPICallError);
            console.log(data);
            setTrainingCompliance(data);
            var complianceData = [];
            var totalComplianceCount = 0;
            var totalNotComplianceCount = 0;
            var expiringComplianceCount = 0;
            var expiredComplianceCount = 0;
            data.forEach(compliance => {
                complianceData.push(...compliance.complianceEntries);
                totalComplianceCount += compliance.totalComplianceCount;
                totalNotComplianceCount += compliance.totalNotComplianceCount;
                if (new Date(compliance.expiryDate) <= new Date()) {
                    expiredComplianceCount++;
                } else if (new Date(compliance.expiryDate) <= new Date(new Date().setDate(new Date().getDate() + 30))) {
                    expiringComplianceCount++;
                }
            });
            setOverallCompliance(totalComplianceCount * 100/(totalComplianceCount + totalNotComplianceCount));
            setExpiringCompliance(expiringComplianceCount);
            setExpiredCompliance(expiredComplianceCount);
            setComplianceData(complianceData);
            setComplianceDataLoading(false);
        }

        const fetchProviderComplianceData = async () => {
            setProviderComplianceDataLoading(true);
            const data = await getProviderTrainingCompliance(navContext.handleAPICallError);
            console.log(data);
            setProviderTrainingCompliance(data);
            setProviderComplianceDataLoading(false);
            
            // Calculate new metrics
            let totalComplianceCount = 0;
            let totalNotComplianceCount = 0;
            let providerComplianceData = [];
            let providersWithOverdueTraining = 0;
            
            data.forEach(compliance => {
                if (compliance.complianceEntries && compliance.complianceEntries.length > 0) {
                    providerComplianceData.push(...compliance.complianceEntries);
                }
                totalComplianceCount += compliance.totalComplianceCount;
                totalNotComplianceCount += compliance.totalNotComplianceCount;

                if (compliance.totalNotComplianceCount > 0) {
                    providersWithOverdueTraining++;
                }
                
            });
            
            setProviderComplianceData(providerComplianceData);
            setTotalProviders(data.length);
            setAverageProviderCompliance(totalComplianceCount * 100 / (totalComplianceCount + totalNotComplianceCount));
            setProvidersNeedingAction(providersWithOverdueTraining);
        }

        fetchComplianceData();
        fetchProviderComplianceData();
    }, []); 

    
    return (<>
        <div className="tw-px-xl tw-py-md">
            <div className="tw-border-b tw-border-gray-800 tw-pb-5">
                <div className="tw-flex tw-items-center tw-justify-between">
                    <h2 className="tw-text-lg tw-font-semibold tw-leading-6 tw-text-gray-900">Training Dashboard</h2>
                    <div className="tw-flex tw-items-center tw-gap-4">
                        <div className="tw-flex tw-items-center tw-gap-2">
                            {/* <span className="tw-text-sm tw-text-gray-600">Compliance Type:</span> */}
                            <div className="tw-flex tw-bg-gray-100 tw-rounded-lg tw-p-1">
                                <button
                                    onClick={() => setComplianceType('client')}
                                    className={`tw-px-3 tw-py-1 tw-text-sm tw-font-medium tw-rounded-md tw-transition-colors ${
                                        complianceType === 'client'
                                            ? 'tw-bg-white tw-text-gray-900 tw-shadow-sm'
                                            : 'tw-text-gray-600 hover:tw-text-gray-900'
                                    }`}
                                >
                                    Client Compliance
                                </button>
                                <button
                                    onClick={() => setComplianceType('provider')}
                                    className={`tw-px-3 tw-py-1 tw-text-sm tw-font-medium tw-rounded-md tw-transition-colors ${
                                        complianceType === 'provider'
                                            ? 'tw-bg-white tw-text-gray-900 tw-shadow-sm'
                                            : 'tw-text-gray-600 hover:tw-text-gray-900'
                                    }`}
                                >
                                    Provider Compliance
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {complianceType === 'client' && (
                <>
            {complianceDataLoading && <div className="tw-flex tw-justify-center tw-items-center tw-h-full">
                <div className="tw-animate-spin tw-rounded-full tw-border-t-2 tw-border-b-2 tw-border-primary tw-w-10 tw-h-10 tw-mt-10"></div>
            </div>}

            {!complianceDataLoading && complianceData.length > 0 && (
                <>
            <div className="tw-mt-8 tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-6">
                {/* Overall Compliance */}
                <div className="tw-bg-green-50 tw-border tw-border-green-200 tw-rounded-lg tw-p-6 tw-flex tw-items-center tw-gap-4">
                    <div className="tw-flex-1">
                        <div className="tw-text-gray-500 tw-text-sm">Overall Compliance</div>
                        <div className="tw-text-2xl tw-font-bold tw-text-gray-900">{overallCompliance.toFixed(2)}%</div>
                        <div className="tw-text-gray-400 tw-text-xs">All active certifications</div>
                    </div>
                    <div>
                        <span className="tw-inline-flex tw-items-center tw-justify-center tw-bg-green-100 tw-rounded-full tw-w-10 tw-h-10">
                            <ChartNoAxesColumnIncreasing className="tw-w-6 tw-h-6 tw-text-green-500" />
                        </span>
                    </div>
                </div>
                {/* Expiring ≤30d */}
                <div className="tw-bg-yellow-50 tw-border tw-border-yellow-200 tw-rounded-lg tw-p-6 tw-flex tw-items-center tw-gap-4">
                    <div className="tw-flex-1">
                        <div className="tw-text-gray-500 tw-text-sm">Expiring ≤ 30d</div>
                        <div className="tw-text-2xl tw-font-bold tw-text-gray-900">{expiringCompliance}</div>
                        <div className="tw-text-gray-400 tw-text-xs">Certifications expiring soon</div>
                    </div>
                    <div>
                        <span className="tw-inline-flex tw-items-center tw-justify-center tw-bg-yellow-100 tw-rounded-full tw-w-10 tw-h-10">
                            <Clock4 className="tw-w-6 tw-h-6 tw-text-yellow-500" />
                        </span>
                    </div>
                </div>
                {/* Expired/Blocked */}
                <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-p-6 tw-flex tw-items-center tw-gap-4">
                    <div className="tw-flex-1">
                        <div className="tw-text-gray-500 tw-text-sm">Expired/Blocked</div>
                        <div className="tw-text-2xl tw-font-bold tw-text-gray-900">{expiredCompliance}</div>
                        <div className="tw-text-gray-400 tw-text-xs">Requires immediate action</div>
                    </div>
                    <div>
                        <span className="tw-inline-flex tw-items-center tw-justify-center tw-bg-red-100 tw-rounded-full tw-w-10 tw-h-10">
                            <Shapes className="tw-w-6 tw-h-6 tw-text-red-500" />
                        </span>
                    </div>
                </div>
                {/* Client-specific Gaps */}
                {/* <div className="tw-bg-cyan-50 tw-border tw-border-cyan-200 tw-rounded-lg tw-p-6 tw-flex tw-items-center tw-gap-4">
                    <div className="tw-flex-1">
                        <div className="tw-text-gray-500 tw-text-sm">Client-specific Gaps</div>
                        <div className="tw-text-2xl tw-font-bold tw-text-gray-900">2</div>
                        <div className="tw-text-gray-400 tw-text-xs">Missing client requirements</div>
                    </div>
                    <div>
                        <span className="tw-inline-flex tw-items-center tw-justify-center tw-bg-cyan-100 tw-rounded-full tw-w-10 tw-h-10">
                            <Building className="tw-w-6 tw-h-6 tw-text-cyan-500" />
                        </span>
                    </div>
                </div> */}
            </div>

            <div className="tw-mt-8 tw-flex tw-gap-4">
                {/* <FilterSidebar /> */}
                <div className="tw-flex-1">
                    <TrainingComplianceDashboard complianceData={trainingCompliance} />
                    {/* <TrainingComplianceComparison complianceData={trainingCompliance} /> */}
                </div>
            </div>
            </>
            )}
            </>
            )}

            {complianceType === 'provider' && (
                <>
                {providerComplianceDataLoading && <div className="tw-flex tw-justify-center tw-items-center tw-h-full"> 
                    <div className="tw-animate-spin tw-rounded-full tw-border-t-2 tw-border-b-2 tw-border-primary tw-w-10 tw-h-10 tw-mt-10"></div>
                </div>}

                {!providerComplianceDataLoading && providerComplianceData.length > 0 && (
                    <>
                     <div className="tw-mt-8 tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-6">
                            {/* Average Compliance % */}
                            <div className="tw-bg-green-50 tw-border tw-border-green-200 tw-rounded-lg tw-p-6 tw-flex tw-items-center tw-gap-4">
                                <div className="tw-flex-1">
                                    <div className="tw-text-gray-500 tw-text-sm">Average Compliance %</div>
                                    <div className="tw-text-2xl tw-font-bold tw-text-gray-900">{averageProviderCompliance.toFixed(2)}%</div>
                                    <div className="tw-text-gray-400 tw-text-xs">Average of all providers compliance percentage</div>
                                </div>
                                <div>
                                    <span className="tw-inline-flex tw-items-center tw-justify-center tw-bg-green-100 tw-rounded-full tw-w-10 tw-h-10">
                                        <ChartNoAxesColumnIncreasing className="tw-w-6 tw-h-6 tw-text-green-500" />
                                    </span>
                                </div>
                            </div>
                            {/* Providers Needing Action */}
                            <div className="tw-bg-yellow-50 tw-border tw-border-yellow-200 tw-rounded-lg tw-p-6 tw-flex tw-items-center tw-gap-4">
                                <div className="tw-flex-1">
                                    <div className="tw-text-gray-500 tw-text-sm">Providers Needing Action</div>
                                    <div className="tw-text-2xl tw-font-bold tw-text-gray-900">{providersNeedingAction}</div>
                                    <div className="tw-text-gray-400 tw-text-xs">Number of providers with ≥ 1 overdue training</div>
                                </div>
                                <div>
                                    <span className="tw-inline-flex tw-items-center tw-justify-center tw-bg-yellow-100 tw-rounded-full tw-w-10 tw-h-10">
                                        <Clock4 className="tw-w-6 tw-h-6 tw-text-yellow-500" />
                                    </span>
                                </div>
                            </div>
                            {/* Total Number of Providers */}
                            <div className="tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg tw-p-6 tw-flex tw-items-center tw-gap-4">
                                <div className="tw-flex-1">
                                    <div className="tw-text-gray-500 tw-text-sm">Total Number of Providers</div>
                                    <div className="tw-text-2xl tw-font-bold tw-text-gray-900">{totalProviders}</div>
                                    <div className="tw-text-gray-400 tw-text-xs">Total providers in list</div>
                                </div>
                                <div>
                                    <span className="tw-inline-flex tw-items-center tw-justify-center tw-bg-blue-100 tw-rounded-full tw-w-10 tw-h-10">
                                        <Building className="tw-w-6 tw-h-6 tw-text-blue-500" />
                                    </span>
                                </div>
                            </div>
                    </div>

                    <div className="tw-mt-8 tw-flex tw-gap-4">
                        <div className="tw-flex-1">
                            <ProviderTrainingComplianceDashboard complianceData={providerTrainingCompliance} />
                        </div>
                    </div>
                    </>
                )}
                </>
            )}
        </div>
    </>
)
}

export default TrainingDashboardPage;